/*!
 * YouTube tracking for Snowplow v4.0.2 (http://bit.ly/sp-js)
 * Copyright 2022 Snowplow Analytics Ltd
 * Licensed under BSD-3-Clause
 */

import { __assign, __rest } from 'tslib';
import { MediaEventType as MediaEventType$1, SnowplowMediaPlugin, startMediaTracking, endMediaTracking, trackMediaSelfDescribingEvent, trackMediaReady, updateMediaTracking, trackMediaSeekStart, trackMediaSeekEnd, trackMediaVolumeChange, trackMediaEnd, trackMediaPause, trackMediaPlay, trackMediaBufferStart, trackMediaQualityChange, trackMediaError, trackMediaPlaybackRateChange, trackMediaBufferEnd } from '@snowplow/browser-plugin-media';
import { v4 } from 'uuid';

/** Type of media player event */
var MediaEventType;
(function (MediaEventType) {
    // Controlling the playback
    /** Media player event fired when the media tracking is successfully attached to the player and can track events. */
    MediaEventType["Ready"] = "ready";
    /** Media player event sent when the player changes state to playing from previously being paused. */
    MediaEventType["Play"] = "play";
    /** Media player event sent when the user pauses the playback. */
    MediaEventType["Pause"] = "pause";
    /** Media player event sent when playback stops when end of the media is reached or because no further data is available. */
    MediaEventType["End"] = "end";
    /** Media player event sent when a seek operation begins. */
    MediaEventType["SeekStart"] = "seek_start";
    /** Media player event sent when a seek operation completes. */
    MediaEventType["SeekEnd"] = "seek_end";
    // Changes in playback settings
    /** Media player event sent when the playback rate has changed. */
    MediaEventType["PlaybackRateChange"] = "playback_rate_change";
    /** Media player event sent when the volume has changed. */
    MediaEventType["VolumeChange"] = "volume_change";
    /** Media player event fired immediately after the browser switches into or out of full-screen mode. */
    MediaEventType["FullscreenChange"] = "fullscreen_change";
    /** Media player event fired immediately after the browser switches into or out of picture-in-picture mode. */
    MediaEventType["PictureInPictureChange"] = "picture_in_picture_change";
    // Tracking playback progress
    /** Media player event fired periodically during main content playback, regardless of other API events that have been sent. */
    MediaEventType["Ping"] = "ping";
    /** Media player event fired when a percentage boundary set in options.boundaries is reached */
    MediaEventType["PercentProgress"] = "percent_progress";
    // Ad events
    /** Media player event that signals the start of an ad break. */
    MediaEventType["AdBreakStart"] = "ad_break_start";
    /** Media player event that signals the end of an ad break. */
    MediaEventType["AdBreakEnd"] = "ad_break_end";
    /** Media player event that signals the start of an ad. */
    MediaEventType["AdStart"] = "ad_start";
    /** Media player event fired when a quartile of ad is reached after continuous ad playback at normal speed. */
    MediaEventType["AdFirstQuartile"] = "ad_first_quartile";
    /** Media player event fired when a midpoint of ad is reached after continuous ad playback at normal speed. */
    MediaEventType["AdMidpoint"] = "ad_midpoint";
    /** Media player event fired when a quartile of ad is reached after continuous ad playback at normal speed. */
    MediaEventType["AdThirdQuartile"] = "ad_third_quartile";
    /** Media player event that signals the ad creative was played to the end at normal speed. */
    MediaEventType["AdComplete"] = "ad_complete";
    /** Media player event fired when the user activated a skip control to skip the ad creative. */
    MediaEventType["AdSkip"] = "ad_skip";
    /** Media player event fired when the user clicked on the ad. */
    MediaEventType["AdClick"] = "ad_click";
    /** Media player event fired when the user clicked the pause control and stopped the ad creative. */
    MediaEventType["AdPause"] = "ad_pause";
    /** Media player event fired when the user resumed playing the ad creative after it had been stopped or paused. */
    MediaEventType["AdResume"] = "ad_resume";
    // Data quality events
    /** Media player event fired when the player goes into the buffering state and begins to buffer content. */
    MediaEventType["BufferStart"] = "buffer_start";
    /** Media player event fired when the the player finishes buffering content and resumes playback. */
    MediaEventType["BufferEnd"] = "buffer_end";
    /** Media player event tracked when the video playback quality changes automatically. */
    MediaEventType["QualityChange"] = "quality_change";
    /** Media player event tracked when the resource could not be loaded due to an error.  */
    MediaEventType["Error"] = "error";
})(MediaEventType || (MediaEventType = {}));
/** Type of media content. */
var MediaType;
(function (MediaType) {
    /** Audio content. */
    MediaType["Audio"] = "audio";
    /** Video content. */
    MediaType["Video"] = "video";
})(MediaType || (MediaType = {}));
/** Type of ads within the break */
var MediaAdBreakType;
(function (MediaAdBreakType) {
    /** Take full control of the video for a period of time. */
    MediaAdBreakType["Linear"] = "linear";
    /** Run concurrently to the video. */
    MediaAdBreakType["NonLinear"] = "nonlinear";
    /** Accompany the video but placed outside the player. */
    MediaAdBreakType["Companion"] = "companion";
})(MediaAdBreakType || (MediaAdBreakType = {}));

var PLAYER_TYPE = 'com.youtube-youtube';
function buildPlayerEntity(conf) {
    var player = conf.player;
    if (!player)
        return;
    return {
        currentTime: player.getCurrentTime(),
        duration: player.getDuration(),
        ended: player.getPlayerState() === YT.PlayerState.ENDED,
        fullscreen: undefined,
        label: conf.config.label,
        livestream: undefined,
        loop: undefined,
        mediaType: MediaType.Video,
        muted: player.isMuted(),
        paused: player.getPlayerState() !== YT.PlayerState.PLAYING,
        pictureInPicture: undefined,
        playbackRate: player.getPlaybackRate(),
        playerType: PLAYER_TYPE,
        quality: player.getPlaybackQuality(),
        volume: player.getVolume(),
    };
}
function buildYouTubeEntity(conf) {
    var player = conf.player, _a = conf.urlParameters, urlParameters = _a === void 0 ? {} : _a;
    if (!player)
        return null;
    var state = player.getPlayerState();
    var data = {
        autoPlay: urlParameters.autoplay === '1',
        avaliablePlaybackRates: player.getAvailablePlaybackRates(),
        avaliableQualityLevels: player.getAvailableQualityLevels(),
        buffering: state === YT.PlayerState.BUFFERING,
        controls: urlParameters.controls !== '0',
        cued: state === YT.PlayerState.CUED,
        loaded: parseInt(String(player.getVideoLoadedFraction() * 100)),
        playbackQuality: player.getPlaybackQuality(),
        playerId: player.getIframe().id,
        unstarted: state === YT.PlayerState.UNSTARTED,
        url: player.getVideoUrl(),
    };
    var spherical = player.getSphericalProperties();
    if (spherical)
        data = __assign(__assign({}, data), spherical);
    var playlistIndex = player.getPlaylistIndex();
    if (playlistIndex !== -1)
        data.playlistIndex = playlistIndex;
    var playlist = player.getPlaylist();
    if (playlist) {
        data.playlist = playlist.map(function (item) { return parseInt(item, 10); });
    }
    return {
        schema: 'iglu:com.youtube/youtube/jsonschema/1-0-0',
        data: data,
    };
}

// URLSearchParams is not supported in IE
// https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
function addUrlParam(url, key, value) {
    var urlParams = parseUrlParams(url);
    urlParams[key] = value;
    return "".concat(url.split('?').shift(), "?").concat(urlParamsToString(urlParams));
}
function parseUrlParams(url) {
    var params = {};
    var urlParams = url.split('?').slice(1).join('?');
    if (!urlParams)
        return params;
    urlParams.split('&').forEach(function (p) {
        var param = p.split('=');
        var key = decodeURIComponent(param.shift());
        params[key] = decodeURIComponent(param.join('='));
    });
    return params;
}
function urlParamsToString(urlParams) {
    // convert an object of url parameters to a string
    return Object.keys(urlParams)
        .map(function (p) { return "".concat(encodeURIComponent(p), "=").concat(encodeURIComponent(urlParams[p])); })
        .join('&');
}

var _a;
var DEFAULT_BOUNDARIES = [10, 25, 50, 75];
var DEFAULT_UPDATE_RATE_MS = 500;
var YTState;
(function (YTState) {
    YTState["UNSTARTED"] = "unstarted";
    YTState["ENDED"] = "ended";
    YTState["PLAYING"] = "playing";
    YTState["PAUSED"] = "paused";
    YTState["BUFFERING"] = "buffering";
    YTState["CUED"] = "cued";
})(YTState || (YTState = {}));
var AllEvents = Object.keys(MediaEventType$1).map(function (k) { return MediaEventType$1[k]; });
var DefaultEvents = [
    MediaEventType$1.Ready,
    MediaEventType$1.Pause,
    MediaEventType$1.Play,
    MediaEventType$1.Ping,
    MediaEventType$1.End,
    MediaEventType$1.SeekStart,
    MediaEventType$1.SeekEnd,
    MediaEventType$1.VolumeChange,
    MediaEventType$1.QualityChange,
    MediaEventType$1.PlaybackRateChange,
    MediaEventType$1.PercentProgress,
];
var LEGACY_EVENTS = {
    play: MediaEventType$1.Play,
    pause: MediaEventType$1.Pause,
    seek: MediaEventType$1.SeekEnd,
    volumechange: MediaEventType$1.VolumeChange,
    ended: MediaEventType$1.End,
    error: MediaEventType$1.Error,
    percentprogress: MediaEventType$1.PercentProgress,
    playbackratechange: MediaEventType$1.PlaybackRateChange,
    playbackqualitychange: MediaEventType$1.QualityChange,
};
var CaptureEventToYouTubeEvent = (_a = {},
    _a[MediaEventType$1.PlaybackRateChange] = 'onPlaybackRateChange',
    _a[MediaEventType$1.QualityChange] = 'onPlaybackQualityChange',
    _a[MediaEventType$1.Error] = 'onError',
    _a[YTState.UNSTARTED] = 'onStateChange',
    _a[MediaEventType$1.End] = 'onStateChange',
    _a[YTState.ENDED] = 'onStateChange',
    _a[YTState.PLAYING] = 'onStateChange',
    _a[MediaEventType$1.Play] = 'onStateChange',
    _a[YTState.PAUSED] = 'onStateChange',
    _a[MediaEventType$1.Pause] = 'onStateChange',
    _a[YTState.BUFFERING] = 'onStateChange',
    _a[YTState.CUED] = 'onStateChange',
    _a);
function trackingOptionsParser(_a, LOG) {
    var video = _a.video, options = __rest(_a, ["video"]);
    if (!video)
        throw Error('Property `id` is undefined');
    var _b = options.captureEvents, captureEvents = _b === void 0 ? ['DefaultEvents'] : _b, boundaries = options.boundaries, config = __rest(options, ["captureEvents", "boundaries"]);
    var parsedEvents = [];
    for (var _i = 0, captureEvents_1 = captureEvents; _i < captureEvents_1.length; _i++) {
        var ev = captureEvents_1[_i];
        // If an event is an EventGroup, get the events from that group
        if (ev === 'AllEvents') {
            parsedEvents = AllEvents;
            break;
        }
        else if (ev === 'DefaultEvents') {
            parsedEvents.push.apply(parsedEvents, DefaultEvents);
        }
        else if (ev in LEGACY_EVENTS) {
            parsedEvents.push(LEGACY_EVENTS[ev]);
        }
        else if (AllEvents.indexOf(ev) !== -1) {
            parsedEvents.push(ev);
        }
        else {
            LOG === null || LOG === void 0 ? void 0 : LOG.error("'".concat(ev, "' is not a supported event"));
        }
    }
    // Check if any events in `conf.captureEvents` require a listener on a `YTPlayerEvent`
    // for them to fire
    var youtubeEvents = [];
    for (var _c = 0, parsedEvents_1 = parsedEvents; _c < parsedEvents_1.length; _c++) {
        var ev = parsedEvents_1[_c];
        if (CaptureEventToYouTubeEvent.hasOwnProperty(ev) && CaptureEventToYouTubeEvent[ev]) {
            youtubeEvents.push(CaptureEventToYouTubeEvent[ev]);
        }
    }
    var progress = undefined;
    // If "percentprogress" is in `captureEvents`, add the `progress` object
    // to the tracking options with the user-defined boundaries, or the default
    if (parsedEvents.indexOf(MediaEventType$1.PercentProgress) !== -1) {
        progress = boundaries || DEFAULT_BOUNDARIES;
        // We need to monitor state changes for 'percent_progress' events
        // as `progressHandler` uses these to set/reset timeouts
        if (youtubeEvents.indexOf('onStateChange') === -1) {
            youtubeEvents.push('onStateChange');
        }
    }
    return {
        sessionId: config.id,
        config: config,
        video: video,
        captureEvents: parsedEvents.filter(function (e, i, a) { return a.indexOf(e) === i; }),
        youtubeEvents: youtubeEvents.filter(function (e, i, a) { return a.indexOf(e) === i; }),
        boundaries: progress,
        updateRate: DEFAULT_UPDATE_RATE_MS,
    };
}

var YouTubeIFrameAPIURL = 'https://www.youtube.com/iframe_api';
var trackedPlayers = {};
var trackingQueue = [];
var legacyApiSessions = [];
var LOG = undefined;
/**
 * Create a YouTubeTrackingPlugin instance.
 * @returns The created YouTubeTrackingPlugin instance
 */
function YouTubeTrackingPlugin() {
    return __assign(__assign({}, SnowplowMediaPlugin()), { logger: function (logger) {
            LOG = logger;
        } });
}
/**
 * Start media tracking for the given player and configuration.
 * @param args Tracking configuration
 * @returns The media session ID for the player
 * @since 4.0.0
 */
function startYouTubeTracking(args) {
    var _a, _b;
    try {
        var conf_1 = trackingOptionsParser(args);
        addPlayer(conf_1);
        startMediaTracking(__assign(__assign({}, conf_1.config), { id: conf_1.sessionId, captureEvents: conf_1.captureEvents, boundaries: conf_1.boundaries, pings: (_a = conf_1.config.pings) !== null && _a !== void 0 ? _a : conf_1.captureEvents.indexOf(MediaEventType$1.Ping) !== -1, context: ((_b = args.context) !== null && _b !== void 0 ? _b : []).concat([function () { return buildYouTubeEntity(conf_1); }]), timestamp: args.timestamp }));
        return conf_1.sessionId;
    }
    catch (e) {
        if (e instanceof Error) {
            LOG === null || LOG === void 0 ? void 0 : LOG.error(e.message);
        }
    }
}
/**
 * Disable tracking for a previously enabled player's media session.
 * @param id The media session ID to disable tracking for
 * @since 4.0.0
 */
function endYouTubeTracking(id) {
    endMediaTracking({ id: id });
    var intervalId = trackedPlayers[id].pollTracking.interval;
    if (intervalId !== null)
        clearInterval(intervalId);
    delete trackedPlayers[id];
}
/**
 * Start media tracking for the given player and configuration.
 * This is a legacy v3 API that wraps the newer `startYouTubeTracking` API.
 * @param args Tracking configuration
 * @returns The media session ID for the player
 * @deprecated since v4.0.0; use {@link startYouTubeTracking}
 */
function enableYouTubeTracking(args) {
    var id = args.id, options = args.options, rest = __rest(args, ["id", "options"]);
    var migrated = __assign(__assign(__assign({ id: v4() }, rest), options), { video: id });
    var sessionId = startYouTubeTracking(migrated);
    if (sessionId) {
        legacyApiSessions.push(sessionId);
        return sessionId;
    }
}
/**
 * Disable tracking for a previously enabled player's media session.
 * This is a legacy v3 API that wraps the newer `endYouTubeTracking` API.
 * @param id The media session ID to disable tracking for, or the oldest known session ID if not provided
 * @since 4.0.0
 * @deprecated since v4.0.0; use {@link endYouTubeTracking}
 */
function disableYouTubeTracking(id) {
    if (id && legacyApiSessions.indexOf(id) !== -1) {
        // id provided, remove from active list
        legacyApiSessions.splice(legacyApiSessions.indexOf(id), 1);
    }
    else {
        // id was not provided; presume we're ending the earliest session we know of
        id = id !== null && id !== void 0 ? id : legacyApiSessions.shift();
    }
    if (id) {
        endYouTubeTracking(id);
    }
}
/**
 * Track a custom Self Describing Event with the Media plugin's entities included, like the events generated by the plugin directly.
 * @param event The tracking options, including the custom Self Describing Event to track and optional media configuration such as `ad` data.
 */
function trackYouTubeSelfDescribingEvent(event) {
    var _a;
    var config = trackedPlayers[event.id];
    if (config) {
        var yt = buildYouTubeEntity(config.conf);
        trackMediaSelfDescribingEvent(__assign(__assign({}, event), { player: buildPlayerEntity(config.conf), context: ((_a = event.context) !== null && _a !== void 0 ? _a : []).concat(yt !== null && yt !== void 0 ? yt : []) }));
    }
}
function addPlayer(conf) {
    var player = conf.video;
    if (typeof player === 'string') {
        // First check if the API is already active and a player exists with this name
        // Creating an additional player will overwrite the existing one and fail
        if (typeof YT !== 'undefined' && 'get' in YT) {
            var existing = YT.get(player);
            if (existing) {
                conf.video = existing;
                return addPlayer(conf);
            }
        }
        // May have meant a DOM ID?
        var el = document.getElementById(player);
        if (!el || el.tagName.toUpperCase() !== 'IFRAME') {
            // Technically the IFrame API will replace non-iframe elements with an iframe
            // In that case we expect the iframe/player to be passed directly instead
            throw Error('Cannot find YouTube iframe or player');
        }
        conf.video = el;
        return addPlayer(conf);
    }
    else if ('tagName' in player) {
        if (player.tagName.toUpperCase() !== 'IFRAME')
            throw Error('Only existing YouTube iframe elements are supported');
        conf.urlParameters = parseUrlParams(ensureYouTubeIframeAPI(player));
        // put them into a queue that will have listeners added once the API is ready
        // and start trying to load the iframe API
        trackingQueue.push(conf);
        installYouTubeIframeAPI();
    }
    else {
        conf.urlParameters = parseUrlParams(player.getIframe().src);
        conf.player = player;
        initializePlayer(conf);
    }
}
function ensureYouTubeIframeAPI(el) {
    // The 'enablejsapi' parameter is required to be '1' for the API to be able to communicate with the player
    // It can be a URL parameter or an attribute of the iframe
    // We only enable it if it hasn't been explicitly disabled/set
    if (el.src.indexOf('enablejsapi') === -1 && el.getAttribute('enablejsapi') == null) {
        var apiEnabled = addUrlParam(el.src, 'enablejsapi', '1');
        // `origin` is recommended when using `enablejsapi`
        if (apiEnabled.indexOf('origin=') === -1 && location.origin) {
            apiEnabled = addUrlParam(apiEnabled, 'origin', location.origin);
        }
        el.src = apiEnabled;
    }
    return el.src;
}
function installYouTubeIframeAPI(iframeAPIRetryWaitMs) {
    if (iframeAPIRetryWaitMs === void 0) { iframeAPIRetryWaitMs = 100; }
    // Once the API is ready to use, 'YT.Player' will be defined
    // 'YT.Player' is not available immediately after 'YT' is defined,
    // so we need to wait until 'YT' is defined to then check 'YT.Player'
    if (typeof YT === 'undefined' || typeof YT.Player === 'undefined') {
        // First we check if the script tag exists in the DOM, and enable the API if not
        var scriptTags = Array.prototype.slice.call(document.getElementsByTagName('script'));
        if (!scriptTags.some(function (s) { return s.src === YouTubeIFrameAPIURL; })) {
            // Load the Iframe API
            // https://developers.google.com/youtube/iframe_api_reference
            var tag = document.createElement('script');
            tag.src = YouTubeIFrameAPIURL;
            var firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }
        // The iframe API usually loads a second script, so `onload` is not reliable
        // We prefer not to pollute global scope so won't use onYouTubeIframeAPIReady either
        // Poll the API to see when it exists
        if (iframeAPIRetryWaitMs <= 6400) {
            setTimeout(installYouTubeIframeAPI, iframeAPIRetryWaitMs, iframeAPIRetryWaitMs * 2);
        }
        else {
            LOG === null || LOG === void 0 ? void 0 : LOG.error('YouTube iframe API failed to load');
        }
    }
    else {
        // Once the API is available, listeners are attached to anything sitting in the queue
        while (trackingQueue.length) {
            initializePlayer(trackingQueue.pop());
        }
    }
}
/**
 * Adds a player to the list of tracked players, and sets up the `onReady` callback to attach listeners once the player is ready
 *
 * @param conf - The configuration for the player
 */
function initializePlayer(conf) {
    var attach = function (_a) {
        var target = _a.target;
        conf.player = target;
        trackedPlayers[conf.sessionId] = {
            player: target,
            conf: conf,
            // initial state for polling listeners
            pollTracking: {
                interval: null,
                prevTime: target.getCurrentTime() || 0,
                prevVolume: target.getVolume() || 0,
            },
        };
        attachListeners(conf);
    };
    if (!conf.player) {
        // Player API not yet initialized
        if (typeof conf.video !== 'string' && 'tagName' in conf.video) {
            conf.player = new YT.Player(conf.video, {
                events: {
                    onReady: attach,
                },
            });
        }
        else
            throw Error('Could not access YouTube Player API');
    }
    else if (typeof conf.player.getCurrentTime === 'function') {
        // Player API initialized and already "ready"
        attach({ target: conf.player });
    }
    else {
        // Player API initialized but not yet "ready"
        conf.player.addEventListener('onReady', attach);
    }
}
function attachListeners(conf) {
    var id = conf.sessionId, player = conf.player;
    var buffering = false;
    // object lookup should be faster than captureEvents.indexOf every time
    var flags = conf.captureEvents.reduce(function (a, e) { return ((a[e] = true), a); }, {});
    var builtInEvents = {
        onStateChange: function (e) {
            if (buffering && e.data !== YT.PlayerState.BUFFERING && flags[MediaEventType$1.BufferEnd]) {
                trackMediaBufferEnd({ id: id });
                buffering = false;
            }
            switch (e.data) {
                case YT.PlayerState.CUED:
                    return;
                case YT.PlayerState.UNSTARTED:
                    return flags[MediaEventType$1.Ready] && trackMediaReady({ id: id });
                case YT.PlayerState.BUFFERING:
                    buffering = true;
                    return flags[MediaEventType$1.BufferEnd] && trackMediaBufferStart({ id: id }); // not a typo
                case YT.PlayerState.PLAYING:
                    return flags[MediaEventType$1.Play] && trackMediaPlay({ id: id });
                case YT.PlayerState.PAUSED:
                    return flags[MediaEventType$1.Pause] && trackMediaPause({ id: id });
                case YT.PlayerState.ENDED:
                    return flags[MediaEventType$1.End] && trackMediaEnd({ id: id });
            }
        },
        onPlaybackQualityChange: function (e) {
            return flags[MediaEventType$1.QualityChange] && trackMediaQualityChange({ id: id, newQuality: e.data });
        },
        onApiChange: function () { },
        onError: function (e) {
            return flags[MediaEventType$1.Error] &&
                trackMediaError({ id: id, errorCode: String(e.data), errorName: YT.PlayerError[e.data] });
        },
        onPlaybackRateChange: function () {
            return flags[MediaEventType$1.PlaybackRateChange] &&
                trackMediaPlaybackRateChange({ id: id, newRate: player.getPlaybackRate() });
        },
        onReady: function () { },
    };
    // The 'ready' event is required for modelling purposes, the Out-The-Box YouTube modelling won't work without it
    // Ensure you have 'ready' in your 'captureEvents' array if you are using the Out-The-Box YouTube modelling
    if (flags[MediaEventType$1.Ready]) {
        // We need to manually trigger the 'ready' event, as it is already in that state and won't call the event listener
        // do it in a new task because we probably haven't called startMediaTracking yet
        setTimeout(function () { return trackMediaReady({ id: conf.sessionId, player: buildPlayerEntity(conf) }); }, 0);
    }
    conf.youtubeEvents.forEach(function (youtubeEventName) {
        player.addEventListener(youtubeEventName, function (e) {
            updateMediaTracking({ id: id, player: buildPlayerEntity(conf) });
            builtInEvents[youtubeEventName](e);
        });
    });
    // there are actually `onVideoProgress` and `onVolumeChange` events but they
    // are not documented so we will poll
    installPollingListeners(conf, flags);
}
function installPollingListeners(conf, flags) {
    var pollState = trackedPlayers[conf.sessionId].pollTracking;
    var player = conf.player;
    var id = conf.sessionId;
    var enableSeek = flags[MediaEventType$1.SeekStart] || flags[MediaEventType$1.SeekEnd];
    var enableVolume = flags[MediaEventType$1.VolumeChange];
    if (!pollState.interval && player) {
        pollState.interval = setInterval(function () {
            updateMediaTracking({ id: id, player: buildPlayerEntity(conf) });
            // Seek Tracking
            if (enableSeek) {
                var playerTime = player.getCurrentTime();
                if (Math.abs(playerTime - (pollState.prevTime + conf.updateRate / 1000)) > 1) {
                    trackMediaSeekStart({ id: id, player: { currentTime: pollState.prevTime } });
                    trackMediaSeekEnd({ id: id, player: { currentTime: playerTime } });
                }
                pollState.prevTime = playerTime;
            }
            // Volume Tracking
            if (enableVolume) {
                var playerVolume = player.getVolume();
                if (playerVolume !== pollState.prevVolume) {
                    trackMediaVolumeChange({ id: id, previousVolume: pollState.prevVolume, newVolume: playerVolume });
                }
                pollState.prevVolume = playerVolume;
            }
        }, conf.updateRate);
    }
}

export { YouTubeTrackingPlugin, disableYouTubeTracking, enableYouTubeTracking, endYouTubeTracking, startYouTubeTracking, trackYouTubeSelfDescribingEvent };
//# sourceMappingURL=index.module.js.map
